import { DownOutlined, UploadOutlined } from '@ant-design/icons';
import { Image2FilledIcon, SailBoatIcon } from '@marine/icons';
import { Button, Dropdown, Menu, Spin } from 'antd';
import cn from 'classnames';
import React, { FC, Suspense, useCallback, useMemo, useState } from 'react';
import { FileCard, useFilesUrlWithToken } from 'services';
import styles from './CoverImage.module.scss';

const FilePicker = React.lazy(() => import('../FilePicker'));

export type CoverImageProps = {
  /**
   * Text displayed at the bottom of the card besides buttons
   */
  title?: string | null;
  /**
   * No cover image title
   */
  noCoverTitle?: string;
  /**
   * Class name to add to container element
   */
  className?: string;
  readOnly?: boolean;
  coverImage?: any;
  id?: string;
  disabled?: boolean;
  bottomComponent?: React.ReactNode;
  onChange?: (value?: any) => void;
  onFinish?: (value?: any) => void;
};

export const CoverImage: FC<CoverImageProps> = ({
  title,
  noCoverTitle = 'Select custom cover image',
  className,
  coverImage,
  readOnly,
  onChange = (_value: any) => {},
  disabled,
  bottomComponent,
  onFinish,
}) => {
  const { fileUrlWithCroppingParamAndToken } = useFilesUrlWithToken();
  const [filePickerVisible, setFilePickerVisible] = useState(false);
  const initialValue = useMemo(() => (coverImage ? { ...coverImage, type: 'image' } : undefined), [coverImage]);
  const handleChangeCover = useCallback(() => {
    setFilePickerVisible(true);
  }, []);

  const handleRemoveCover = useCallback(() => {
    onChange(null);
    onFinish?.(null);
  }, [onChange, onFinish]);

  const handleOnOkay = (items: FileCard[]) => {
    onChange(items[0]);
    onFinish?.(items[0]);
  };

  const titleMemo = useMemo(
    () => (
      <div className={styles.titleContainer}>
        <span className={styles.footerText}>{title}</span>
      </div>
    ),
    [title],
  );

  const _handleOnDeletedFiles = useCallback(
    (deletedFileIds: string[]) => {
      if (initialValue && deletedFileIds.includes(initialValue?.id)) {
        onChange(null);
      }
    },
    [initialValue, onChange],
  );

  const imageUrl = useMemo(() => {
    if (coverImage) return fileUrlWithCroppingParamAndToken(coverImage?.url, { format: 'cover-sm' });
  }, [coverImage, fileUrlWithCroppingParamAndToken]);

  return (
    <div className={styles.container}>
      {imageUrl ? (
        <>
          <div className={cn(styles.imageWrapper, className)}>
            <img src={imageUrl} alt="Cover" className={styles.image} />
            {!disabled && (
              <div className={styles.dropdown}>
                <Dropdown.Button
                  disabled={readOnly}
                  icon={<DownOutlined />}
                  trigger={['click']}
                  dropdownRender={() => (
                    <Menu
                      items={[
                        {
                          key: 'remove-cover',
                          label: 'Remove image',
                          onClick: handleRemoveCover,
                          style: { height: '22px' },
                          danger: true,
                        },
                      ]}
                    />
                  )}
                  onClick={handleChangeCover}
                >
                  <Image2FilledIcon /> Change
                </Dropdown.Button>
              </div>
            )}
          </div>
          {title && <div className={styles.footerShadow}>{titleMemo}</div>}
        </>
      ) : (
        <>
          <div className={cn(styles.imageWrapper, styles.placeholder, className)}>
            <div className={styles.iconContainer}>
              <SailBoatIcon />
            </div>

            <div className={styles.subtitle}>{noCoverTitle || 'No image available...'}</div>

            {!disabled && (
              <Button
                onClick={handleChangeCover}
                icon={<UploadOutlined />}
                className={styles.uploadBtn}
                disabled={readOnly}
              >
                Select image
              </Button>
            )}
          </div>

          {title && titleMemo}
        </>
      )}

      {!disabled && !readOnly && filePickerVisible && (
        <Suspense fallback={<Spin spinning fullscreen />}>
          <FilePicker
            title="Change cover image"
            initialSelectedFiles={initialValue ? [initialValue] : []}
            close={() => setFilePickerVisible(false)}
            onFinish={handleOnOkay}
            open={filePickerVisible}
            accepted="image"
          />
        </Suspense>
      )}
      {bottomComponent && <div className={styles.bottomComponent}>{bottomComponent}</div>}
      {!bottomComponent && title && <div className={styles.bottomComponent}>{titleMemo}</div>}
    </div>
  );
};
